/**
 * Public landing page users hit when they click the confirmation link in
 * the verification email. Renders a big "Confirm my email" button and only
 * POSTs to /api/security/email/verify on click — email safety scanners
 * (Outlook ATP / Safe Links, Gmail Postmaster, Mimecast) don't execute
 * click handlers, so the token isn't burned by their pre-fetch. On success
 * we show the welcome message + Sign-in CTA.
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, ArrowRight, RefreshCw, Mail } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { toast } from "sonner";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ status: token ? "ready" : "missing", msg: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  const confirm = async () => {
    if (!token) return;
    setBusy(true);
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const r = await api.post("/security/email/verify", { token });
      setState({ status: "ok", msg: "", email: r?.data?.email || "" });
    } catch (e) {
      setState({ status: "fail", email: "",
                 msg: e?.response?.data?.detail || "This verification link is invalid, expired or already used." });
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (!state.email) { toast("Head to sign-in and hit 'Resend' from there"); return; }
    setResending(true);
    try {
      await api.post("/security/email/resend-public", { email: state.email });
      toast.success("A fresh verification link is on the way");
    } catch { toast.error("Could not send — try again shortly"); }
    finally { setResending(false); }
  };

  return (
    <div className="min-h-screen fl-noise fl-page flex items-center justify-center p-6" style={{ background: "var(--fl-bg)" }}>
      <div className="w-full max-w-md text-center">
        <Link to="/" className="flex justify-center mb-10"><Logo variant="wordmark" size="lg" /></Link>
        <div className="fl-card p-8" data-testid="verify-email-card">

          {state.status === "missing" && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                <XCircle className="text-red-400" size={32} />
              </div>
              <h1 className="fl-heading text-2xl mt-5">Missing token</h1>
              <p className="text-sm text-zinc-400 mt-2">Open the link straight from your email — the token got stripped.</p>
              <Link to="/login" className="fl-btn-ghost mt-6 w-full inline-flex justify-center text-sm">Back to sign in</Link>
            </>
          )}

          {state.status === "ready" && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-[#f47a20]/12 border border-[#f47a20]/40 flex items-center justify-center">
                <Mail className="text-[#f47a20]" size={28} />
              </div>
              <div className="fl-label text-[#f47a20] mt-5">One more tap</div>
              <h1 className="fl-heading text-3xl mt-2">Confirm your email</h1>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                We just need you to press the button below to complete verification.
              </p>
              <button
                onClick={confirm}
                disabled={busy}
                className="fl-btn mt-6 w-full inline-flex justify-center items-center gap-2"
                data-testid="verify-email-confirm"
              >
                <CheckCircle size={14} /> {busy ? "Verifying…" : "Confirm my email"}
              </button>
              <div className="text-[11px] text-zinc-500 mt-4">
                Why the extra click? Some email apps (like Outlook) pre-check links for safety and would otherwise consume this one-time code before you got a chance to use it.
              </div>
            </>
          )}

          {state.status === "loading" && (
            <>
              <Loader2 className="mx-auto animate-spin text-[#f47a20]" size={28} />
              <div className="mt-4 text-sm text-zinc-400">Verifying your email…</div>
            </>
          )}

          {state.status === "ok" && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                <CheckCircle className="text-emerald-400" size={32} data-testid="verify-email-ok" />
              </div>
              <div className="fl-label text-emerald-400 mt-5">Welcome to Functional LAB</div>
              <h1 className="fl-heading text-3xl mt-2">You're all set.</h1>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                Your email is verified and your account is live. Sign in to complete a quick 60-second onboarding — then you're in.
              </p>
              <Link
                to="/login"
                className="fl-btn mt-6 inline-flex justify-center items-center gap-2 w-full"
                data-testid="verify-email-signin"
              >
                Sign in now <ArrowRight size={13} />
              </Link>
              <div className="text-[11px] text-zinc-500 mt-4">
                Need help? Email us at{" "}
                <a href="mailto:functional.lab25@gmail.com" className="text-[#f47a20] hover:underline">functional.lab25@gmail.com</a>
              </div>
            </>
          )}

          {state.status === "fail" && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                <XCircle className="text-red-400" size={32} data-testid="verify-email-fail" />
              </div>
              <h1 className="fl-heading text-2xl mt-5">Verification failed</h1>
              <p className="text-sm text-zinc-400 mt-2">{state.msg}</p>
              <button
                onClick={resend}
                disabled={resending}
                className="fl-btn mt-6 w-full inline-flex justify-center items-center gap-2"
                data-testid="verify-email-resend"
              >
                <RefreshCw size={13} className={resending ? "animate-spin" : ""} />
                {resending ? "Sending…" : "Request a new link"}
              </button>
              <Link to="/login" className="fl-btn-ghost mt-3 w-full inline-flex justify-center text-sm" data-testid="verify-email-back">
                Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
